import type { RecaptchaOptions, RecaptchaShowOptions } from '../typings';
import { BaseCaptcha } from './base';

const LocalMap: Record<string, string> = {
  en: 'en',
  'en-US': 'en',
  'zh-CN': 'zh-CN',
  'zh-TW': 'zh-TW',
  'ja-JP': 'ja',
  'ru-RU': 'ru',
  'es-ES': 'es',
  'vi-VN': 'vi',
  'tr-TR': 'tr',
  'pt-BR': 'pt',
  'pt-PT': 'pt',
  'fr-FR': 'fr',
  'th-TH': 'th',
  'id-ID': 'id',
  'kk-KZ': '',
  'fil-PH': 'fil',
  'uk-UA': 'uk',
  'hi-IN': 'hi',
};

export class Recaptcha extends BaseCaptcha {
  constructor(options: RecaptchaOptions) {
    super(options);

    this.key = 'recaptcha';
    this.siteKey = options.recaptchaSiteKey;
    this.defaultLang = 'en';
    this.lang = options.lang || this.defaultLang;
    this.injectFunction = null;
    if (process.env.NODE_ENV === 'production') {
      this.setUrl();
    }
  }

  // resize dom size
  resize(el: HTMLElement | string | null) {
    let dom: HTMLElement | null;
    if (typeof el === 'string') {
      dom = document.querySelector(`#${el}`);
    } else {
      dom = el;
    }
    if (dom) {
      const parent = dom.parentElement;
      if (parent) {
        const scale = parent.clientWidth / 304;
        dom.style.transform = `scale(${scale})`;
      }
    }
  }

  /**
   * Set language
   *
   * @param {string} lang
   * @memberof Recaptcha
   */
  setLang(lang: string) {
    this.lang = lang || this.defaultLang;
    this.setUrl();
  }

  /**
   * set load url for different language
   *
   * @memberof Recaptcha
   */
  setUrl() {
    const lang = LocalMap[this.lang] || 'en';
    this.url = `https://www.google.com/recaptcha/api.js?render=explicit&hl=${lang}`;
  }

  /**
   * Refresh recaptcha
   *
   * @memberof Recaptcha
   */
  refresh() {
    try {
      window.grecaptcha.reset();
    } catch (e) {
      console.warn(`[${this.key}] Refresh error`);
    }
  }

  initGContainer(options: RecaptchaShowOptions) {
    const { id, callback, theme } = options;
    const dom = document.createElement('div');
    dom.id = id;
    dom.style.position = 'fixed';
    dom.style.top = '75px';
    dom.dataset.sitekey = this.siteKey;

    dom.classList.add('g-recaptcha');

    this.recaptchaElm = dom;

    document.body.appendChild(this.recaptchaElm);
  }

  /**
   * Render catpcha
   * @param {RecaptchaShowOptions} options
   * @return {*}
   * @memberof Recaptcha
   */
  render(options: RecaptchaShowOptions) {
    const { id, callback, theme } = options;

    if (!this.recaptchaElm || !document.querySelector(`#${id}`)) {
      this.initGContainer(options);
    }

    this.id = id;

    const captchaSize = this.options.invisibleMode ? 'invisible' : 'normal';
    /* eslint-disable @typescript-eslint/no-this-alias */
    const self = this;
    const initPms = new Promise((resolve) => {
      // If can not load in 30s, resolve false
      setTimeout(() => {
        resolve(false);
      }, 30000);

      window.grecaptcha.ready(() => {
        self.captcha = window.grecaptcha.render(id, {
          classes: 'zs-test',
          sitekey: self.siteKey,
          size: 'invisible',
          badge: 'inline',
          theme: theme || 'light',
          callback(recaptchaToken: string, abc: any) {
            if (self?.injectFunction) {
              self?.injectFunction({
                type: self.key,
                g_recaptcha_response: recaptchaToken,
              });
            } else {
              callback &&
                callback({ type: self.key, recaptcha: recaptchaToken });
            }
            window.grecaptcha.reset(self.captcha);
          },
          'expired-callback': () => {
            callback({
              type: self.key,
              error: `[${self.key}] ReCAPTCHA expired`,
            });
            window.grecaptcha.reset(self.captcha);
          },
          'error-callback': () => {
            callback({
              type: self.key,
              error: `[${self.key}] ReCAPTCHA error`,
            });
            window.grecaptcha.reset(self.captcha);
          },
        });
        resolve(true);
      });
    });

    return initPms;
  }

  async initialize(options: RecaptchaShowOptions) {
    const loadRes = await this.load(this.url);

    if (!loadRes) {
      return false;
    }
    const initRes = await this.render(options);
    if (!initRes) {
      console.warn(`[${this.key}] Initialize error`);
    }
    this.ready = true;
    return initRes;
  }

  async show(options: RecaptchaShowOptions) {
    if (options.callback) {
      this.injectFunction = options.callback;
    }

    try {
      // Invisible recaptcha need to call window.grecaptcha.execute to show
      if (this.options.invisibleMode) {
        const res = await window.grecaptcha.execute(this.captcha);
        return true;
      }
      const res = await this.initialize(options);
      return res;
    } catch (e) {
      console.warn(`[${this.key}] ${e}`);
      return false;
    }
  }
}
